"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.generateRecurringValues = generateRecurringValues;
exports.getWeekday = getWeekday;
exports.getAllEvents = getAllEvents;
exports.generateEventId = generateEventId;
exports.getPrimaryCalendar = getPrimaryCalendar;
exports.busySlotData = busySlotData;
exports.getBusyIntervals = getBusyIntervals;
const core_1 = require("@hcengineering/core");
const _1 = __importDefault(require("."));
function getInstance(event, date) {
    const diff = event.dueDate - event.date;
    return {
        ...event,
        recurringEventId: event.eventId,
        date,
        dueDate: date + diff,
        originalStartTime: date,
        _class: _1.default.class.ReccuringInstance,
        eventId: generateEventId(),
        _id: (0, core_1.generateId)(),
        virtual: true
    };
}
function generateRecurringValues(rule, startDate, from, to) {
    const currentDate = new Date(startDate);
    switch (rule.freq) {
        case 'DAILY':
            return generateDailyValues(rule, currentDate, from, to);
        case 'WEEKLY':
            return generateWeeklyValues(rule, currentDate, from, to);
        case 'MONTHLY':
            return generateMonthlyValues(rule, currentDate, from, to);
        case 'YEARLY':
            return generateYearlyValues(rule, currentDate, from, to);
        default:
            throw new Error('Invalid recurring rule frequency');
    }
}
function generateDailyValues(rule, currentDate, from, to) {
    const values = [];
    const { count, endDate, interval } = rule;
    const { bySetPos } = rule;
    let i = 0;
    while (true) {
        if (bySetPos == null || bySetPos.includes(getSetPos(currentDate))) {
            const res = currentDate.getTime();
            if (currentDate.getTime() > to)
                break;
            if (endDate != null && currentDate.getTime() > endDate)
                break;
            if (res >= from && res <= to) {
                values.push(res);
            }
            i++;
        }
        currentDate.setDate(currentDate.getDate() + (interval ?? 1));
        if (count !== undefined && i === count)
            break;
    }
    return values;
}
function generateWeeklyValues(rule, currentDate, from, to) {
    const values = [];
    const { count, endDate, interval } = rule;
    let { byDay, bySetPos } = rule;
    let i = 0;
    byDay ??= [getWeekday(currentDate)];
    while (true) {
        const next = new Date(currentDate).setDate(currentDate.getDate() + (interval ?? 1) * 7);
        const end = new Date(new Date(currentDate).setDate(currentDate.getDate() + 7));
        let date = currentDate;
        while (date < end) {
            if (endDate != null && date.getTime() > endDate)
                return values;
            if (date.getTime() > to)
                return values;
            if ((byDay == null || matchesByDay(date, byDay)) && (bySetPos == null || bySetPos.includes(getSetPos(date)))) {
                const res = date.getTime();
                if (res >= from && res <= to) {
                    values.push(res);
                }
                i++;
            }
            date = new Date(date.setDate(date.getDate() + 1));
            if (count !== undefined && i === count)
                return values;
        }
        currentDate = new Date(next);
    }
}
function matchesByDay(date, byDay) {
    const weekday = getWeekday(date);
    const dayOfMonth = Math.floor((date.getDate() - 1) / 7) + 1;
    for (const byDayItem of byDay) {
        if (byDayItem === weekday) {
            return true;
        }
        const pos = parseInt(byDayItem);
        if (isNaN(pos))
            continue;
        if (pos > 0 && dayOfMonth === pos) {
            return true;
        }
        if (pos < 0 && dayOfMonth === getNegativePosition(date, weekday, pos)) {
            return true;
        }
    }
    return false;
}
function getNegativePosition(date, weekday, pos) {
    const lastDayOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
    const occurrences = [];
    for (let day = lastDayOfMonth; day >= 1; day--) {
        const tempDate = new Date(date.getFullYear(), date.getMonth(), day);
        if (getWeekday(tempDate) === weekday) {
            occurrences.push(day);
        }
        if (occurrences.length === Math.abs(pos)) {
            return occurrences.pop();
        }
    }
    throw new Error(`Unable to calculate negative position ${pos}`);
}
function generateMonthlyValues(rule, currentDate, from, to) {
    const values = [];
    const { count, endDate, interval } = rule;
    let { byDay, byMonthDay, bySetPos } = rule;
    let i = 0;
    if (byDay == null && byMonthDay == null) {
        byMonthDay = [currentDate.getDate()];
    }
    while (true) {
        const next = new Date(currentDate).setMonth(currentDate.getMonth() + (interval ?? 1), 1);
        const end = new Date(new Date(currentDate).setMonth(currentDate.getMonth() + 1, 1));
        let date = currentDate;
        const candidates = [];
        // If no specific rules are set except byMonthDay (simple monthly recurrence)
        if (byDay == null && bySetPos == null && byMonthDay != null) {
            for (const day of byMonthDay) {
                const originalDate = new Date(currentDate.getTime());
                const sameDate = new Date(Date.UTC(currentDate.getUTCFullYear(), currentDate.getUTCMonth(), day, originalDate.getUTCHours(), originalDate.getUTCMinutes(), originalDate.getUTCSeconds(), originalDate.getUTCMilliseconds()));
                if (sameDate.getUTCMonth() === currentDate.getUTCMonth()) {
                    // Valid day for this month
                    candidates.push(sameDate);
                }
            }
        }
        else {
            while (date < end) {
                if ((byDay == null || matchesByDay(date, byDay)) &&
                    (byMonthDay == null || byMonthDay.includes(date.getDate()))) {
                    candidates.push(new Date(date));
                }
                date = new Date(date.setDate(date.getDate() + 1));
            }
        }
        let filtered = candidates;
        if (bySetPos != null) {
            filtered = bySetPos
                .map((pos) => {
                if (pos === 0)
                    return null; // invalid
                const index = pos > 0 ? pos - 1 : candidates.length + pos;
                return candidates[index] ?? null;
            })
                .filter((d) => d != null);
        }
        for (const d of filtered) {
            const res = d.getTime();
            if (res >= from && res <= to) {
                values.push(res);
                i++;
                if (count !== undefined && i === count)
                    return values;
            }
        }
        if (endDate != null && next > endDate)
            return values;
        if (next >= to)
            return values;
        currentDate = new Date(next);
    }
}
function generateYearlyValues(rule, currentDate, from, to) {
    const values = [];
    const { count, endDate, interval } = rule;
    const { byDay, byMonthDay, byYearDay, byWeekNo, byMonth, bySetPos } = rule;
    let i = 0;
    while (true) {
        const next = new Date(currentDate).setFullYear(currentDate.getFullYear() + (interval ?? 1), 0, 1);
        const end = new Date(new Date(currentDate).setFullYear(currentDate.getFullYear() + 1, 0, 1));
        let date = currentDate;
        const candidates = [];
        // If no specific rules are set, only generate an event for the same month/day each year
        if (byDay == null &&
            byMonthDay == null &&
            byYearDay == null &&
            byWeekNo == null &&
            byMonth == null &&
            bySetPos == null) {
            const originalDate = new Date(currentDate.getTime());
            const sameDate = new Date(Date.UTC(currentDate.getUTCFullYear(), originalDate.getUTCMonth(), originalDate.getUTCDate(), originalDate.getUTCHours(), originalDate.getUTCMinutes(), originalDate.getUTCSeconds(), originalDate.getUTCMilliseconds()));
            candidates.push(sameDate);
        }
        else {
            while (date < end) {
                if ((byDay == null || matchesByDay(date, byDay)) &&
                    (byMonthDay == null || byMonthDay.includes(date.getDate())) &&
                    (byYearDay == null || byYearDay.includes(getYearDay(date))) &&
                    (byWeekNo == null || byWeekNo.includes(getWeekNumber(date))) &&
                    (byMonth == null || byMonth.includes(date.getMonth()))) {
                    const res = date.getTime();
                    if (res >= from && res <= to) {
                        candidates.push(new Date(res));
                    }
                    i++;
                }
                date = new Date(date.setDate(date.getDate() + 1));
            }
        }
        let filtered = candidates;
        if (bySetPos != null) {
            filtered = bySetPos
                .map((pos) => {
                if (pos === 0)
                    return null; // invalid
                const index = pos > 0 ? pos - 1 : candidates.length + pos;
                return candidates[index] ?? null;
            })
                .filter((d) => d != null);
        }
        for (const d of filtered) {
            const res = d.getTime();
            if (res >= from && res <= to) {
                values.push(res);
                i++;
                if (count !== undefined && i === count)
                    return values;
            }
        }
        if (endDate != null && next > endDate)
            return values;
        if (next >= to)
            return values;
        currentDate = new Date(currentDate.setFullYear(currentDate.getFullYear() + (interval ?? 1)));
    }
}
function getSetPos(date) {
    const month = date.getMonth();
    const year = date.getFullYear();
    const firstOfMonth = new Date(year, month, 1);
    const daysOffset = firstOfMonth.getDay();
    const day = date.getDate();
    return Math.ceil((day + daysOffset) / 7);
}
/**
 * @public
 */
function getWeekday(date) {
    const weekdays = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'];
    const weekday = weekdays[date.getDay()];
    return weekday;
}
function getWeekNumber(date) {
    const firstDayOfYear = new Date(date.getFullYear(), 0, 1);
    const daysOffset = firstDayOfYear.getDay();
    const diff = (date.getTime() - firstDayOfYear.getTime()) / 86400000;
    return Math.floor((diff + daysOffset + 1) / 7);
}
function getYearDay(date) {
    const startOfYear = new Date(date.getFullYear(), 0, 0);
    const diff = date.getTime() - startOfYear.getTime();
    return Math.floor(diff / 86400000);
}
function getReccuringEventInstances(event, instances, from, to) {
    let res = [];
    for (const rule of event.rules ?? []) {
        const values = generateRecurringValues(rule, event.date, from, to);
        for (const val of values) {
            const instance = getInstance(event, val);
            res.push(instance);
        }
    }
    for (const date of event.rdate ?? []) {
        if (date < from || date > to)
            continue;
        const instance = getInstance(event, date);
        const exists = res.find((p) => p.date === instance.date);
        if (exists === undefined)
            res.push(instance);
    }
    const excludes = new Set(event.exdate ?? []);
    res = res.filter((p) => !excludes.has(p.date));
    res = res.filter((i) => {
        const override = instances.find((p) => p.originalStartTime === i.originalStartTime);
        return override === undefined;
    });
    return res;
}
/**
 * @public
 */
function getAllEvents(events, from, to) {
    const base = [];
    const recur = [];
    const instances = [];
    const recurData = [];
    const instancesMap = new Map();
    for (const event of events) {
        if (event._class === _1.default.class.ReccuringEvent) {
            recur.push(event);
        }
        else if (event._class === _1.default.class.ReccuringInstance) {
            const instance = event;
            instances.push(instance);
            const arr = instancesMap.get(instance.recurringEventId) ?? [];
            arr.push(instance);
            instancesMap.set(instance.recurringEventId, arr);
        }
        else {
            if (from >= event.dueDate)
                continue;
            if (event.date >= to)
                continue;
            base.push(event);
        }
    }
    for (const rec of recur) {
        const recEvents = getReccuringEventInstances(rec, instancesMap.get(rec.eventId) ?? [], from, to);
        recurData.push(...recEvents);
    }
    const res = [
        ...base,
        ...recurData,
        ...instances.filter((p) => {
            return from <= p.dueDate && p.date <= to && p.isCancelled !== true;
        })
    ].map((it) => ({ ...it, date: Math.max(from, it.date), dueDate: Math.min(to, it.dueDate) }));
    res.sort((a, b) => a.date - b.date);
    return res;
}
/**
 * @public
 */
function generateEventId() {
    const id = (0, core_1.generateId)();
    return encodeToBase32Hex(id);
}
function encodeToBase32Hex(input) {
    const encoder = new TextEncoder();
    const bytes = encoder.encode(input);
    const base32HexDigits = '0123456789abcdefghijklmnopqrstuv';
    let result = '';
    for (let i = 0; i < bytes.length; i += 5) {
        const octets = [
            (bytes[i] ?? 0) >> 3,
            ((bytes[i] & 0x07) << 2) | (bytes[i + 1] >> 6),
            (bytes[i + 1] >> 1) & 0x1f,
            ((bytes[i + 1] & 0x01) << 4) | (bytes[i + 2] >> 4),
            ((bytes[i + 2] & 0x0f) << 1) | (bytes[i + 3] >> 7),
            (bytes[i + 3] >> 2) & 0x1f,
            ((bytes[i + 3] & 0x03) << 3) | (bytes[i + 4] >> 5),
            bytes[i + 4] & 0x1f
        ];
        for (let j = 0; j < 8; j++) {
            result += base32HexDigits.charAt(octets[j]);
        }
    }
    return result;
}
function getPrimaryCalendar(calendars, preference, acc) {
    if (preference?.attachedTo !== undefined) {
        const pref = calendars.find((p) => p._id === preference.attachedTo);
        if (pref !== undefined)
            return pref._id;
    }
    for (const _calendar of calendars) {
        if (_calendar._class === _1.default.class.ExternalCalendar &&
            !_calendar.hidden &&
            _calendar.default) {
            return _calendar._id;
        }
    }
    return `${acc}_calendar`;
}
/**
 * @public
 *
 * Slot fields mirroring an event. Shared by the server trigger and the migration: both must write
 * the very same shape, or `getDiffUpdate` (which skips `undefined`) freezes the difference forever.
 *
 * `overrides` are the occurrence starts a series gave away to persisted instances - the slot has no
 * notion of an instance, so a master must exclude them the same way `getAllEvents` does.
 */
function busySlotData(event, person, overrides = []) {
    const rec = event;
    // An override replaces exactly one occurrence, but it inherits `rules` from the master it was
    // cut out of - keeping them would block the whole series at the overridden time.
    const single = event.recurringEventId !== undefined;
    return {
        person,
        eventId: event.eventId,
        date: event.date,
        dueDate: event.dueDate,
        allDay: event.allDay,
        // Empty rather than undefined: getDiffUpdate skips undefined, so turning a public event
        // private would otherwise leave its title on the slot forever.
        title: event.visibility === 'public' ? event.title : '',
        timeZone: event.timeZone,
        // Left unset, not emptied: the client splits slots into plain and recurring by
        // `rules: { $exists: ... }`, and an override is a single occurrence like any plain one.
        rules: single ? undefined : rec.rules,
        exdate: single
            ? undefined
            : rec.rules !== undefined
                ? Array.from(new Set([...(rec.exdate ?? []), ...overrides]))
                : rec.exdate,
        rdate: single ? undefined : rec.rdate
    };
}
/**
 * @public
 *
 * Expands busy slots into plain intervals within the window, merging overlaps
 * per person. Recurring slots are expanded from their rules.
 */
function getBusyIntervals(slots, from, to) {
    const res = new Map();
    for (const slot of slots) {
        const intervals = res.get(slot.person) ?? [];
        for (const value of getSlotOccurrences(slot, from, to)) {
            intervals.push({ date: Math.max(from, value), dueDate: Math.min(to, value + slot.dueDate - slot.date) });
        }
        res.set(slot.person, intervals);
    }
    for (const [person, intervals] of res) {
        // A recurring slot may have no occurrence inside the window at all - such a person is free.
        if (intervals.length === 0)
            res.delete(person);
        else
            res.set(person, mergeIntervals(intervals));
    }
    return res;
}
function getSlotOccurrences(slot, from, to) {
    const duration = slot.dueDate - slot.date;
    if ((slot.rules ?? []).length === 0 && (slot.rdate ?? []).length === 0) {
        return slot.date < to && slot.dueDate > from ? [slot.date] : [];
    }
    const values = new Set();
    // A slot starting before `from` may still be running inside the window.
    const start = from - duration;
    for (const rule of slot.rules ?? []) {
        for (const value of generateRecurringValues(rule, slot.date, start, to)) {
            values.add(value);
        }
    }
    for (const date of slot.rdate ?? []) {
        if (date >= start && date <= to)
            values.add(date);
    }
    for (const date of slot.exdate ?? []) {
        values.delete(date);
    }
    return Array.from(values).filter((it) => it + duration > from);
}
function mergeIntervals(intervals) {
    intervals.sort((a, b) => a.date - b.date);
    const res = [];
    for (const interval of intervals) {
        const last = res[res.length - 1];
        if (last !== undefined && interval.date <= last.dueDate) {
            last.dueDate = Math.max(last.dueDate, interval.dueDate);
        }
        else {
            res.push({ ...interval });
        }
    }
    return res;
}
//# sourceMappingURL=utils.js.map